import { gsap } from 'gsap'
import { Observer } from 'gsap/Observer'

gsap.registerPlugin(Observer)

/**
 * InfiniteColumns
 *
 * A wall of vertical columns that loops forever. One virtual position drives
 * every column; each column reads it at its own speed, so the wall never moves
 * as a single block.
 *
 * Motion model — drift and input are added to the same number, so there is no
 * "autoplay" mode to start and stop:
 *
 *   target  += drift * dt        // always running
 *   target  += input             // wheel / touch / keys
 *   current += (target - current) * (1 - e^(-ease * dt))
 *
 * Looping is clone-and-wrap, not cell recycling. Each column holds one `__loop`
 * of measured height H, cloned until the column is taller than the viewport
 * plus one loop. Rendering is then a single wrap per column.
 *
 * The column COUNT is not decided here — it is read from CSS by the caller and
 * passed to build(). See components/home-scroller.js.
 *
 * Expected DOM:
 *
 * <div class="home-scroller js-home-scroller">
 *   <div class="js-home-scroller-viewport"></div>   <- columns are built here
 *   <div class="js-home-scroller-deck" hidden>      <- source cards
 *     <div class="home-scroller__card">…</div>
 *   </div>
 * </div>
 */
export class InfiniteColumns {
	constructor(el, options = {}) {
		this.el = el

		this.options = {
			minPerColumn: 3, // cards each column must end up with
			drift: 22, // idle speed, px/sec
			ease: 6, // lerp rate; higher catches up faster
			wheelMultiplier: 1,
			keyMultiplier: 1,
			speeds: [1, 0.86, 1.14, 0.93, 1.07], // per column, indexed modulo
			// Observer reports touch drags opposite to wheel: dragging the content
			// up is a downward scroll. Inverted by default so the two agree.
			invertTouch: true,
			// Input below this (px) does not steer the drift — a few pixels of
			// finger jitter at the end of a gesture should not reverse the wall.
			directionThreshold: 2,
			...options,
		}

		this.viewport = this.el.querySelector('.js-home-scroller-viewport')
		this.deckEl = this.el.querySelector('.js-home-scroller-deck')

		// The source deck. Read once and kept in memory: build() empties the
		// viewport on every rebuild, so it needs a pristine list to deal from.
		this.deck = Array.from(this.deckEl?.children || [])

		this.columns = []
		this.columnCount = 0

		this.target = 0
		this.current = 0
		this.velocity = 0

		this.drift = this.prefersReducedMotion() ? 0 : this.options.drift
		// Which way the wall coasts once input stops. Steered by the last
		// meaningful input, so releasing an upward scroll keeps drifting up
		// rather than snapping back to the default direction.
		this.driftDirection = 1

		this.bndRender = this.render.bind(this)
		this.bndOnKeyDown = this.onKeyDown.bind(this)
	}

	prefersReducedMotion() {
		return window.matchMedia('(prefers-reduced-motion: reduce)').matches
	}

	// ------------------------------------------------------------ //
	// BUILD
	// ------------------------------------------------------------ //

	/**
	 * Deal the deck into `columnCount` columns, round-robin.
	 *
	 * The deck is duplicated first when there are not enough cards to give every
	 * column its minimum — five columns of a four-card deck would otherwise leave
	 * columns holding a single card, which loops too tightly to read as content.
	 */
	build(columnCount) {
		if (!this.viewport || !this.deck.length) return

		this.columnCount = columnCount
		this.viewport.innerHTML = ''
		this.columns = []

		const needed = columnCount * this.options.minPerColumn
		const cards = []
		while (cards.length < needed) {
			this.deck.forEach((card) => cards.push(card.cloneNode(true)))
		}

		for (let i = 0; i < columnCount; i += 1) {
			const column = document.createElement('div')
			column.className = 'home-scroller__column'

			const track = document.createElement('div')
			track.className = 'home-scroller__track'

			const loop = document.createElement('div')
			loop.className = 'home-scroller__loop'

			// Round-robin so consecutive cards land in different columns; two
			// neighbouring columns never show the same card at the same height.
			for (let j = i; j < cards.length; j += columnCount) {
				loop.appendChild(cards[j])
			}

			track.appendChild(loop)
			column.appendChild(track)
			this.viewport.appendChild(column)

			this.columns.push({
				el: column,
				track,
				loop,
				clones: [],
				loopHeight: 0,
				speed: this.options.speeds[i % this.options.speeds.length],
				// Deterministic stagger so the columns do not all start on a card
				// boundary. Irrational-ish step keeps the offsets from repeating.
				offset: i * 137,
			})
		}

		this.measure()
	}

	/**
	 * Measure each loop and clone it until the column covers the viewport plus
	 * one full loop — the headroom that makes the wrap seamless.
	 */
	measure() {
		const viewportHeight = window.innerHeight

		this.columns.forEach((column) => {
			const height = column.loop.offsetHeight
			if (!height) return

			column.loopHeight = height

			const copiesNeeded = Math.ceil(viewportHeight / height) + 1
			const clonesNeeded = Math.max(1, copiesNeeded - 1) // the original counts as one

			while (column.clones.length < clonesNeeded) {
				const clone = column.loop.cloneNode(true)
				clone.setAttribute('aria-hidden', 'true')
				column.track.appendChild(clone)
				column.clones.push(clone)
			}
			while (column.clones.length > clonesNeeded) {
				column.clones.pop().remove()
			}
		})

		this.draw()
	}

	// ------------------------------------------------------------ //
	// RENDER
	// ------------------------------------------------------------ //
	render(time, deltaTime) {
		// Clamp: a backgrounded tab hands back a huge delta on return, which
		// would teleport the wall.
		const dt = Math.min(deltaTime, 50) / 1000

		this.target += this.drift * this.driftDirection * dt

		const previous = this.current
		// Exponential smoothing rather than a fixed lerp factor, so the feel does
		// not change between a 60Hz and a 120Hz display.
		this.current += (this.target - this.current) * (1 - Math.exp(-this.options.ease * dt))
		this.velocity = this.current - previous

		this.draw()
	}

	draw() {
		this.columns.forEach((column) => {
			if (!column.loopHeight) return

			const y = -gsap.utils.wrap(0, column.loopHeight, this.current * column.speed + column.offset)
			column.track.style.transform = `translate3d(0, ${y}px, 0)`
		})
	}

	// ------------------------------------------------------------ //
	// INPUT
	// ------------------------------------------------------------ //

	/**
	 * The one way anything moves the wall. Every source — wheel, touch, keys —
	 * goes through here so they all steer the idle drift the same way.
	 */
	addInput(delta) {
		if (!delta) return

		if (Math.abs(delta) > this.options.directionThreshold) {
			this.driftDirection = delta > 0 ? 1 : -1
		}

		this.target += delta
	}

	initInput() {
		this.observer = Observer.create({
			target: this.el,
			type: 'wheel,touch',
			preventDefault: true,
			tolerance: 0,
			dragMinimum: 3,
			lockAxis: true,
			// Let anything that scrolls on its own keep its own gestures.
			ignore: '.js-nav-overlay',
			onChangeY: (self) => {
				const isTouch = self.event?.type?.startsWith('touch') || self.event?.pointerType === 'touch'
				const direction = this.options.invertTouch && isTouch ? -1 : 1

				this.addInput(self.deltaY * this.options.wheelMultiplier * direction)
			},
		})

		window.addEventListener('keydown', this.bndOnKeyDown)
	}

	/**
	 * Observer has no keyboard support, so the wall would otherwise be
	 * unreachable without a pointer. Keys feed `target` like any other input, so
	 * they ease rather than jump.
	 */
	onKeyDown(event) {
		if (event.metaKey || event.ctrlKey || event.altKey) return

		const target = event.target
		if (target?.closest?.('input, textarea, select, [contenteditable="true"], .js-nav-overlay')) return

		const card = this.columns[0]?.loop?.firstElementChild?.offsetHeight || 300
		const page = window.innerHeight * 0.9
		let delta = 0

		switch (event.key) {
			case 'ArrowDown':
				delta = card
				break
			case 'ArrowUp':
				delta = -card
				break
			case 'PageDown':
				delta = page
				break
			case 'PageUp':
				delta = -page
				break
			case ' ':
				delta = event.shiftKey ? -page : page
				break
			case 'End':
				delta = page * 4
				break
			case 'Home':
				delta = -page * 4
				break
			default:
				return
		}

		event.preventDefault()
		this.addInput(delta * this.options.keyMultiplier)
	}

	// ------------------------------------------------------------ //
	// LIFECYCLE
	// ------------------------------------------------------------ //
	start() {
		if (this.isRunning) return
		this.isRunning = true

		this.initInput()
		gsap.ticker.add(this.bndRender)
	}

	destroy() {
		if (this.isRunning) {
			gsap.ticker.remove(this.bndRender)
			this.isRunning = false
		}

		window.removeEventListener('keydown', this.bndOnKeyDown)

		if (this.observer) {
			this.observer.kill()
			this.observer = null
		}

		if (this.viewport) this.viewport.innerHTML = ''
		this.columns = []
	}
}
